// Verb Groups — collapsible cards grouping every verb by conjugation pattern
// (regular, the three stem-change families, yo-only and fully irregular), each
// with a visual diagram. Ported from the legacy views.groups with three
// transformations: window.SPANISH_DATA.* → direct ES imports, inline
// onclick="navigate(...)" → data-inf + addEventListener, and the global `app`
// → a mount-element parameter.
import { ALL_VERBS, IRREGULAR_FORMS, conjugate } from "../data/index.js";
import { navigate } from "../router/router.js";

export function groupsView(app) {
  // Italian verb families (the data layer tags every verb regular | irregular |
  // isco | reflexive). We subdivide the irregulars into the "-go" group (an
  // io/loro -g- insertion) and the fully-irregular core.
  const regulars = ALL_VERBS.filter((v) => v.group === "regular");
  const iscoVerbs = ALL_VERBS.filter((v) => v.group === "isco");
  const reflexives = ALL_VERBS.filter((v) => v.group === "reflexive");
  const irregulars = ALL_VERBS.filter((v) => v.group === "irregular");

  // "-go" verbs: the present io form (and loro) gains a -g- (tengo, vengo,
  // salgo, rimango, pongo, valgo, scelgo …) while noi / voi stay regular.
  const goVerbs = irregulars.filter((v) => {
    const irr = IRREGULAR_FORMS[v.inf];
    return irr && irr.present && /go$/.test(irr.present[0]) && /gono$/.test(irr.present[5] || "");
  });
  const fullyIrregular = irregulars.filter((v) => !goVerbs.includes(v));

  app.innerHTML = `
    <section>
      <h2 class="text-2xl font-bold text-sage-800 mb-1">Verb Groups</h2>
      <p class="text-sage-600 text-sm mb-6">Italian verbs fall into patterns. Master the patterns and you've mastered hundreds of verbs at once.</p>

      <div class="space-y-4" id="groups-container"></div>
    </section>
  `;

  const container = app.querySelector("#groups-container");

  // ---- Visual diagrams for each group ----

  // Boot diagram: highlights the four "changed" cells (yo, tú, él, ellos)
  // around the unchanged nosotros/vosotros (the "heel" of the boot).
  function bootDiagram(title, regularExample, changedForms) {
    return `
      <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4 mb-3">
        <p class="text-xs text-sage-500 uppercase tracking-wide mb-2">${title} — present tense</p>
        <div class="flex gap-4 items-start flex-wrap">
          <div class="relative">
            <svg width="0" height="0" style="position:absolute"></svg>
            <div class="inline-grid grid-cols-2 gap-1 text-sm font-mono">
              <div class="bg-red-100 text-red-700 px-3 py-1.5 rounded-l-lg border-2 border-red-300 border-r-0">${changedForms.yo}</div>
              <div class="bg-sage-50 text-sage-500 px-3 py-1.5 rounded-r-lg border border-sage-200">${changedForms.nos}</div>
              <div class="bg-red-100 text-red-700 px-3 py-1.5 rounded-l-lg border-2 border-red-300 border-r-0 border-t-0">${changedForms.tu}</div>
              <div class="bg-sage-50 text-sage-500 px-3 py-1.5 rounded-r-lg border border-sage-200 border-t-0">${changedForms.vos}</div>
              <div class="bg-red-100 text-red-700 px-3 py-1.5 rounded-l-lg border-2 border-red-300 border-r-0 border-t-0">${changedForms.el}</div>
              <div class="bg-red-100 text-red-700 px-3 py-1.5 rounded-r-lg border-2 border-red-300 border-l-0 border-t-0">${changedForms.ellos}</div>
            </div>
            <p class="text-xs text-sage-400 mt-2 text-center">👢 the "boot" shape</p>
          </div>
          <div class="text-xs text-sage-600 leading-relaxed max-w-xs">
            <p class="mb-1.5"><span class="inline-block w-2 h-2 bg-red-300 rounded-sm align-middle"></span> <b>Red cells change</b> — the stem shifts in the boot.</p>
            <p class="mb-1.5"><span class="inline-block w-2 h-2 bg-sage-200 rounded-sm align-middle"></span> <b>Grey cells stay regular</b> — noi / voi keep the regular stem.</p>
            <p class="mt-2 italic">Example: <b>${regularExample}</b></p>
          </div>
        </div>
      </div>
    `;
  }

  function regularDiagram() {
    return `
      <div class="bg-gradient-to-br from-sage-50 to-white border border-sage-200 rounded-xl p-4 mb-3">
        <p class="text-xs text-sage-500 uppercase tracking-wide mb-2">The three patterns — present tense</p>
        <div class="grid sm:grid-cols-3 gap-3 text-sm">
          <div class="bg-white rounded-lg border border-sage-100 p-3">
            <p class="font-bold text-sage-800 mb-0.5">-ARE · parlare</p>
            <p class="text-xs text-sage-400 mb-2">to speak</p>
            <div class="font-mono text-xs space-y-0.5 text-sage-700">
              <div>io parl<span class="text-sage-900 font-bold">o</span></div>
              <div>tu parl<span class="text-sage-900 font-bold">i</span></div>
              <div>lui parl<span class="text-sage-900 font-bold">a</span></div>
              <div>noi parl<span class="text-sage-900 font-bold">iamo</span></div>
              <div>voi parl<span class="text-sage-900 font-bold">ate</span></div>
              <div>loro parl<span class="text-sage-900 font-bold">ano</span></div>
            </div>
          </div>
          <div class="bg-white rounded-lg border border-sage-100 p-3">
            <p class="font-bold text-sage-800 mb-0.5">-ERE · credere</p>
            <p class="text-xs text-sage-400 mb-2">to believe</p>
            <div class="font-mono text-xs space-y-0.5 text-sage-700">
              <div>io cred<span class="text-sage-900 font-bold">o</span></div>
              <div>tu cred<span class="text-sage-900 font-bold">i</span></div>
              <div>lui cred<span class="text-sage-900 font-bold">e</span></div>
              <div>noi cred<span class="text-sage-900 font-bold">iamo</span></div>
              <div>voi cred<span class="text-sage-900 font-bold">ete</span></div>
              <div>loro cred<span class="text-sage-900 font-bold">ono</span></div>
            </div>
          </div>
          <div class="bg-white rounded-lg border border-sage-100 p-3">
            <p class="font-bold text-sage-800 mb-0.5">-IRE · dormire</p>
            <p class="text-xs text-sage-400 mb-2">to sleep</p>
            <div class="font-mono text-xs space-y-0.5 text-sage-700">
              <div>io dorm<span class="text-sage-900 font-bold">o</span></div>
              <div>tu dorm<span class="text-sage-900 font-bold">i</span></div>
              <div>lui dorm<span class="text-sage-900 font-bold">e</span></div>
              <div>noi dorm<span class="text-sage-900 font-bold">iamo</span></div>
              <div>voi dorm<span class="text-sage-900 font-bold">ite</span></div>
              <div>loro dorm<span class="text-sage-900 font-bold">ono</span></div>
            </div>
          </div>
        </div>
        <p class="text-xs text-sage-500 mt-3 italic">💡 -ERE and -IRE are identical except for voi (credete vs dormite). Many -ire verbs also take -isco (finire → finisco).</p>
      </div>
    `;
  }

  function yoOnlyDiagram() {
    return `
      <div class="bg-gradient-to-br from-red-50 to-white border border-red-200 rounded-xl p-4 mb-3">
        <p class="text-xs text-sage-500 uppercase tracking-wide mb-2">A -g- appears in the "io" (and loro) form</p>
        <div class="flex gap-4 items-start flex-wrap">
          <div class="inline-grid grid-cols-2 gap-1 text-sm font-mono">
            <div class="bg-red-100 text-red-700 px-3 py-1.5 rounded-lg border-2 border-red-300 font-bold">ten<span class="underline">go</span></div>
            <div class="bg-sage-50 text-sage-500 px-3 py-1.5 rounded-lg border border-sage-200">teniamo</div>
            <div class="bg-sage-50 text-sage-500 px-3 py-1.5 rounded-lg border border-sage-200">tieni*</div>
            <div class="bg-sage-50 text-sage-500 px-3 py-1.5 rounded-lg border border-sage-200">tenete</div>
            <div class="bg-sage-50 text-sage-500 px-3 py-1.5 rounded-lg border border-sage-200">tiene*</div>
            <div class="bg-sage-50 text-sage-500 px-3 py-1.5 rounded-lg border border-sage-200">tengono*</div>
          </div>
          <div class="text-xs text-sage-600 leading-relaxed max-w-xs">
            <p>The "-go" group: <b>tenere, venire, salire, valere, porre, rimanere</b>. Memorize as a chant:</p>
            <p class="mt-2 font-mono text-sage-800">tengo · vengo · salgo<br>valgo · pongo · rimango</p>
            <p class="mt-2 text-sage-400 italic">* the -g- shows up in io and loro (tengo, tengono); tenere/venire also shift e→ie in tu / lui.</p>
          </div>
        </div>
      </div>
    `;
  }

  function fullyIrregularDiagram() {
    return `
      <div class="bg-gradient-to-br from-red-50 to-white border border-red-200 rounded-xl p-4 mb-3">
        <p class="text-xs text-sage-500 uppercase tracking-wide mb-2">The big four — must be memorized</p>
        <div class="grid sm:grid-cols-2 gap-3 text-sm">
          <div class="bg-white rounded-lg border border-red-100 p-3">
            <p class="font-bold text-sage-800 mb-1">essere <span class="text-sage-400 font-normal text-xs">— to be</span></p>
            <div class="font-mono text-xs grid grid-cols-3 gap-x-2 gap-y-0.5">
              <div>io <b class="text-red-700">sono</b></div>
              <div>tu <b class="text-red-700">sei</b></div>
              <div>lui <b class="text-red-700">è</b></div>
              <div>noi <b class="text-red-700">siamo</b></div>
              <div>voi <b class="text-red-700">siete</b></div>
              <div>loro <b class="text-red-700">sono</b></div>
            </div>
          </div>
          <div class="bg-white rounded-lg border border-red-100 p-3">
            <p class="font-bold text-sage-800 mb-1">avere <span class="text-sage-400 font-normal text-xs">— to have</span></p>
            <div class="font-mono text-xs grid grid-cols-3 gap-x-2 gap-y-0.5">
              <div>io <b class="text-red-700">ho</b></div>
              <div>tu <b class="text-red-700">hai</b></div>
              <div>lui <b class="text-red-700">ha</b></div>
              <div>noi <b class="text-red-700">abbiamo</b></div>
              <div>voi avete</div>
              <div>loro <b class="text-red-700">hanno</b></div>
            </div>
          </div>
          <div class="bg-white rounded-lg border border-red-100 p-3">
            <p class="font-bold text-sage-800 mb-1">andare <span class="text-sage-400 font-normal text-xs">— to go</span></p>
            <div class="font-mono text-xs grid grid-cols-3 gap-x-2 gap-y-0.5">
              <div>io <b class="text-red-700">vado</b></div>
              <div>tu <b class="text-red-700">vai</b></div>
              <div>lui <b class="text-red-700">va</b></div>
              <div>noi andiamo</div>
              <div>voi andate</div>
              <div>loro <b class="text-red-700">vanno</b></div>
            </div>
          </div>
          <div class="bg-white rounded-lg border border-red-100 p-3">
            <p class="font-bold text-sage-800 mb-1">fare <span class="text-sage-400 font-normal text-xs">— to do / make</span></p>
            <div class="font-mono text-xs grid grid-cols-3 gap-x-2 gap-y-0.5">
              <div>io <b class="text-red-700">faccio</b></div>
              <div>tu <b class="text-red-700">fai</b></div>
              <div>lui <b class="text-red-700">fa</b></div>
              <div>noi <b class="text-red-700">facciamo</b></div>
              <div>voi <b class="text-red-700">fate</b></div>
              <div>loro <b class="text-red-700">fanno</b></div>
            </div>
          </div>
        </div>
        <p class="text-xs text-sage-500 mt-3 italic">💡 Fun fact: <b>essere</b> and <b>stare</b> share the same past participle — <i>stato</i> — so "I was/have been" is <i>sono stato</i> for both. Context decides.</p>
      </div>
    `;
  }

  function renderGroup(title, color, description, verbs, infographic) {
    const sec = document.createElement("div");
    sec.className = "bg-white rounded-2xl border border-sage-100 p-5";
    sec.innerHTML = `
      <div class="flex items-center justify-between mb-2 cursor-pointer group-header">
        <div>
          <h3 class="font-bold text-lg text-sage-800">${title} <span class="text-sage-400 text-sm font-normal">(${verbs.length})</span></h3>
          <p class="text-sm text-sage-600 mt-1">${description}</p>
        </div>
        <span class="text-sage-400 text-xl group-arrow transition-transform">›</span>
      </div>
      <div class="group-body hidden mt-3">
        ${infographic || ""}
        <p class="text-xs text-sage-500 uppercase tracking-wide mb-2 mt-1">Verbs in this group</p>
        <div class="flex flex-wrap gap-1.5">
          ${verbs
            .slice(0, 80)
            .map(
              (v) => `
            <button data-inf="${v.inf}"
              class="verb-chip text-xs px-2.5 py-1 rounded-full ${color} hover:opacity-80 transition">
              ${v.emoji ? v.emoji + " " : ""}${v.inf}
            </button>
          `
            )
            .join("")}
          ${
            verbs.length > 80
              ? `<span class="text-xs text-sage-400 px-2 py-1">+${verbs.length - 80} more</span>`
              : ""
          }
        </div>
      </div>
    `;
    sec.querySelector(".group-header").addEventListener("click", () => {
      const body = sec.querySelector(".group-body");
      const arrow = sec.querySelector(".group-arrow");
      body.classList.toggle("hidden");
      arrow.style.transform = body.classList.contains("hidden") ? "" : "rotate(90deg)";
    });
    sec.querySelectorAll(".verb-chip").forEach((btn) =>
      btn.addEventListener("click", () =>
        navigate("verb-detail", { inf: btn.dataset.inf })
      )
    );
    container.appendChild(sec);
  }

  renderGroup(
    "Regular verbs",
    "bg-sage-100 text-sage-700",
    "These follow the textbook -are / -ere / -ire patterns with no surprises. Once you know parlare, credere, and dormire, you can conjugate them all.",
    regulars,
    regularDiagram()
  );
  renderGroup(
    "-isco verbs",
    "bg-sand-100 text-sand-700",
    "A large sub-pattern of -ire verbs that insert -isc- in the boot (io / tu / lui / loro) while noi and voi stay plain. Examples: finire → finisco, capire → capisco, preferire → preferisco.",
    iscoVerbs,
    bootDiagram(
      "finire (to finish)",
      "capire → capisco, preferire → preferisco",
      { yo: "finisco", tu: "finisci", el: "finisce", nos: "finiamo", vos: "finite", ellos: "finiscono" }
    )
  );
  renderGroup(
    "“-go” verbs",
    "bg-sand-100 text-sand-700",
    "io and loro gain a -g- (tu / lui often also shift the stem), while noi and voi stay regular. Examples: tenere → tengo, venire → vengo, salire → salgo, rimanere → rimango.",
    goVerbs,
    bootDiagram(
      "tenere (to hold / keep)",
      "venire → vengo, salire → salgo, rimanere → rimango",
      { yo: "tengo", tu: "tieni", el: "tiene", nos: "teniamo", vos: "tenete", ellos: "tengono" }
    )
  );
  if (reflexives.length)
    renderGroup(
      "Reflexive verbs",
      "bg-sand-100 text-sand-700",
      "Verbs whose action falls back on the subject. The infinitive ends in -si (lavarsi), they take a reflexive pronoun (mi / ti / si / ci / vi / si), and their compound tenses always use essere. Examples: lavarsi → mi lavo, svegliarsi → mi sveglio.",
      reflexives,
      yoOnlyDiagram()
    );
  renderGroup(
    "Fully irregular",
    "bg-red-100 text-red-700",
    "These verbs deviate across multiple forms and tenses. The big ones — essere, avere, fare, andare, dare, dire — must simply be memorised.",
    fullyIrregular,
    fullyIrregularDiagram()
  );

  window.scrollTo({ top: 0, behavior: "smooth" });
}
